import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Gallery3D from './components/Gallery3D';
import NavigationControls from './components/NavigationControls';
import PaintingList from './components/PaintingList';
import PaintingInfo from './components/PaintingInfo';
import AudioPlayer from './components/AudioPlayer';
import LoadingScreen from './components/LoadingScreen';
import { mockPaintings, paintingSlug } from './data/mockGalleryData';
import { getInitialTimeOfDay, TIME_IDS } from './components/scene/DayNight';
import { getSavedClimateMode, saveClimateMode, pickNextClimate, pickInitialClimate, randomClimateDuration, createWeatherState, setWindFrom } from './components/scene/Weather';
import { fetchLocalWeather } from './lib/localWeather';
import { eggState, announceEgg, playChime, isBirthdayToday, isOwlHour } from './lib/eggs';
import { celebrateLikes } from './lib/useLikes';
import EggToast from './components/EggToast';
import PaintSplash from './components/PaintSplash';
import VirtualJoystick from './components/VirtualJoystick';
import SecretsHint from './components/SecretsHint';
import { festivalToday } from './lib/festivals';
import { createWeatherSound, getWeatherSoundEnabled, saveWeatherSoundEnabled } from './lib/weatherSound';
import TourOverlay from './components/TourOverlay';
import HoverTooltip from './components/HoverTooltip';
import { isTypingTarget } from './lib/typing';
import { useLikes } from './lib/useLikes';
import GuestbookModal from './components/GuestbookModal';
import TheatrePlayer from './components/theatre/TheatrePlayer';
import TheatreControls from './components/theatre/TheatreControls';
import SceneErrorBoundary from './components/SceneErrorBoundary';

// The Grand Salon is a separate scene, loaded only when a visitor walks through the glass door.
const GrandSalon = lazy(() => import('./components/salon/GrandSalon'));
// Where the camera walks to before the scene changes (just in front of each door).
const HALL_DOOR_WALK = { position: [0, 3, 10.6], lookAt: [0, 2.4, 13] };
const SALON_DOOR_WALK = { position: [0, 3, 15.0], lookAt: [0, 2.4, 17] };

// Theatre mode plays the painting-process videos in order.
const theatrePlaylist = mockPaintings.filter((p) => p.youtubeUrl);
import { Keyboard, Info } from 'lucide-react';
import { HelpCircle } from 'lucide-react';
import HelpModal from './components/HelpModal';
import DonateModal from './components/DonateModal';
import { Heart, BookOpen } from 'lucide-react';
import ImageViewer from './components/ImageViewer';

import './App.css';

// How long the guided tour lingers on each painting.
const TOUR_STEP_MS = 12000;
const SITE_TITLE = "David's Art Gallery";
// How long each climate lasts in auto mode.
// Auto mode: each climate lasts a random time in this range (ms).
const AUTO_CLIMATE_MIN_MS = 35000;
const AUTO_CLIMATE_MAX_MS = 100000;

const GalleryApp = () => {
  // Shareable links: /painting/<slug> opens the gallery focused on that painting.
  const { slug } = useParams();
  const navigate = useNavigate();
  const [linkedPaintingIndex] = useState(() =>
    slug ? mockPaintings.findIndex((p) => paintingSlug(p) === slug) : -1
  );
  const [currentPaintingIndex, setCurrentPaintingIndex] = useState(Math.max(linkedPaintingIndex, 0));
  const [isTourActive, setIsTourActive] = useState(false);
  const [musicPlaySignal, setMusicPlaySignal] = useState(0);
  const isFirstIndexRender = useRef(true);

  // Time of day: every visit starts from the visitor's own clock (morning, afternoon, evening or
  // night); the time-of-day menu (or N) changes it for the rest of the visit.
  const [localTimeOfDay] = useState(getInitialTimeOfDay);
  const [timeOfDay, setTimeOfDay] = useState(localTimeOfDay);
  const isNight = timeOfDay === 'night';
  const nextTimeOfDay = () => setTimeOfDay((t) => TIME_IDS[(TIME_IDS.indexOf(t) + 1) % TIME_IDS.length]);

  // Climate: "auto" cycles through the climates, or the visitor picks one.
  const [climateMode, setClimateMode] = useState(getSavedClimateMode);
  const [climate, setClimate] = useState(() => (climateMode === 'auto' ? pickInitialClimate() : climateMode));
  const climateHistory = useRef([]); // recent climates, so auto mode doesn't repeat itself
  // Live weather state, shared by the 3D scene and the weather sounds.
  const weatherRef = useRef(createWeatherState(climate));
  const [weatherSoundOn, setWeatherSoundOn] = useState(getWeatherSoundEnabled);
  const weatherSound = useRef(null);
  const toggleWeatherSound = () => setWeatherSoundOn((on) => { saveWeatherSoundEnabled(!on); return !on; });
  const changeClimateMode = (mode) => {
    saveClimateMode(mode);
    setClimateMode(mode);
    if (mode !== 'auto') setClimate(mode);
  };

  // Start with the visitor's real local weather (in Auto mode; Auto then carries on changing it
  // at random as usual) and blow the wind the way it blows where they are.
  const [localWeather, setLocalWeather] = useState(null);
  const climateModeRef = useRef(climateMode);
  climateModeRef.current = climateMode;
  useEffect(() => {
    let cancelled = false;
    fetchLocalWeather().then((live) => {
      if (cancelled || !live) return;
      setLocalWeather(live);
      setWindFrom(weatherRef, live.windFrom);
      if (climateModeRef.current === 'auto') {
        climateHistory.current = [];
        setClimate(live.climate);
      }
    });
    return () => { cancelled = true; };
  }, []);

  const [hoveredPainting, setHoveredPainting] = useState(null);
  const [showList, setShowList] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // const [manualMovement, setManualMovement] = useState(null);
  const [focusOnPainting, setFocusOnPainting] = useState(false);
  const [isGuideMode, setIsGuideMode] = useState(false); // Start in free roam mode
  const [floorClickPosition, setFloorClickPosition] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [moveToCenter, setMoveToCenter] = useState(false);

  const [showHelp, setShowHelp] = useState(false);
  const [showDonate, setShowDonate] = useState(false);
  const [showGuestbook, setShowGuestbook] = useState(false);

  // Theatre mode: the camera sits in a recliner, the hall becomes a cinema, videos play.
  const [isTheatre, setIsTheatre] = useState(false);
  const [theatreSeated, setTheatreSeated] = useState(false);
  const [theatreIndex, setTheatreIndex] = useState(0);
  const { likes, toggleLike } = useLikes();


  const [showImageViewer, setShowImageViewer] = useState(false);
  const [viewerPainting, setViewerPainting] = useState(null);

// FIX 1: New state to manage continuous movement from UI buttons.
  // This object tracks which direction buttons are currently being held down.
  const [uiMovement, setUiMovement] = useState({
    forward: false,
    backward: false,
    left: false,
    right: false,
  });

  // Mock classical music URL
  // const audioUrl = "https://commondatastorage.googleapis.com/codeskulptor-assets/Epoq-Lepidoptera.ogg";
  const audioUrl = "/audio/Vivaldi-Spring.mp3";

  const profileImageUrl = "/icons/profile_pic.jpg"; // 160 px, 8 KB
  const profileWebpageUrl = "https://www.facebook.com/artbydavidraj/"; // Replace with your webpage URL


  useEffect(() => {
    // Check if mobile
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Simulate loading time for 3D assets
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 3000);
    
    // Hide instructions after 5 seconds
    const instructionsTimer = setTimeout(() => {
      setShowInstructions(false);
    }, 5000);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(instructionsTimer);
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Shareable links: once the 3D scene is ready, glide to the linked painting.
  useEffect(() => {
    if (slug && linkedPaintingIndex === -1) {
      navigate('/', { replace: true }); // unknown painting – fall back to the hall
      return;
    }
    if (isLoading || linkedPaintingIndex === -1) return;
    const t = setTimeout(() => setFocusOnPainting(true), 800);
    return () => clearTimeout(t);
  }, [isLoading]);

  // --- Grand Salon: open the glass door, walk through, fade, and switch scenes ---------------
  const [galleryScene, setGalleryScene] = useState('hall'); // 'hall' | 'salon'
  const [doorOpen, setDoorOpen] = useState(false);
  const [doorWalk, setDoorWalk] = useState(null);
  const [sceneFade, setSceneFade] = useState(false);
  const [sceneNotice, setSceneNotice] = useState(null);
  const [fadeLabel, setFadeLabel] = useState('');
  const [cameFromSalon, setCameFromSalon] = useState(false);
  const sceneTimers = useRef([]);
  useEffect(() => () => sceneTimers.current.forEach(clearTimeout), []);

  const walkThroughDoor = (walk) => {
    if (doorWalk || sceneFade || isTheatre) return;
    setIsTourActive(false);
    setShowList(false); setShowInfo(false); setShowHelp(false); setShowGuestbook(false); setShowDonate(false);
    setFocusOnPainting(false);
    setFloorClickPosition(null);
    setHoveredPainting(null);
    setUiMovement({ forward: false, backward: false, left: false, right: false });
    setFadeLabel(walk === HALL_DOOR_WALK ? 'Entering the Grand Salon…' : 'Returning to the Glass Hall…');
    setDoorOpen(true);
    sceneTimers.current.push(setTimeout(() => setDoorWalk(walk), 450)); // let the doors swing first
  };
  const enterSalon = () => galleryScene === 'hall' && walkThroughDoor(HALL_DOOR_WALK);
  const exitSalon = () => galleryScene === 'salon' && walkThroughDoor(SALON_DOOR_WALK);

  const handleDoorWalkDone = () => {
    setSceneFade(true);
    sceneTimers.current.push(setTimeout(() => {
      setCameFromSalon(galleryScene === 'salon');
      setGalleryScene((s) => (s === 'hall' ? 'salon' : 'hall'));
      setDoorWalk(null);
      setDoorOpen(false);
      // Safety net: never leave the screen black if a scene is slow to report ready.
      sceneTimers.current.push(setTimeout(() => setSceneFade(false), 25000));
    }, 650));
  };
  // Tap a window: walk up to it and look outside, then hand control back right there.
  const [windowWalk, setWindowWalk] = useState(null);
  const lookOutWindow = (walk) => {
    if (doorWalk || windowWalk || sceneFade || isTheatre || galleryScene !== 'hall') return;
    setIsTourActive(false);
    setShowList(false); setShowInfo(false); setShowHelp(false); setShowGuestbook(false); setShowDonate(false);
    setFocusOnPainting(false);
    setFloorClickPosition(null);
    setHoveredPainting(null);
    setUiMovement({ forward: false, backward: false, left: false, right: false });
    setWindowWalk(walk);
  };
  const windowHover = () => !isMobile && setHoveredPainting({ title: 'A window', subtitle: 'Click to look outside' });

  const [musicSlot, setMusicSlot] = useState(null); // where the music button sits (in the top-right icon column)
  const greeted = useRef(false);
  const [sceneShown, setSceneShown] = useState(false); // the 3D gallery has appeared at least once
  const handleSceneReady = () => {
    setSceneFade(false);
    // once the 3D scene first shows: a greeting for today's festival (decorations: scene/Festivals.jsx)
    const festival = !greeted.current && festivalToday();
    greeted.current = true;
    setSceneShown(true);
    if (festival) {
      sceneTimers.current.push(setTimeout(() => window.dispatchEvent(new CustomEvent('gallery-egg', {
        detail: { id: festival.id, message: festival.greeting, icon: festival.icon, festival: festival.name },
      })), 1500));
    }
  };
  const handleSalonError = () => {
    setGalleryScene('hall');
    setCameFromSalon(true);
    setSceneFade(false);
    setSceneNotice('The Grand Salon could not be loaded right now. Please try again later.');
    sceneTimers.current.push(setTimeout(() => setSceneNotice(null), 5000));
  };
  const doorHover = (label) => () => !isMobile && setHoveredPainting({ title: label, subtitle: 'Click to open the door' });

  const enterTheatre = () => {
    if (galleryScene !== 'hall') return;
    setIsTourActive(false);
    setShowList(false); setShowInfo(false); setShowHelp(false); setShowGuestbook(false); setShowDonate(false);
    setFocusOnPainting(false);
    setHoveredPainting(null);
    setUiMovement({ forward: false, backward: false, left: false, right: false });
    const start = theatrePlaylist.findIndex((p) => p.id === mockPaintings[currentPaintingIndex].id);
    setTheatreIndex(Math.max(start, 0));
    setTheatreSeated(false);
    setIsTheatre(true);
  };
  const exitTheatre = () => { setIsTheatre(false); setTheatreSeated(false); };
  const nextVideo = () => setTheatreIndex((i) => (i + 1) % theatrePlaylist.length);
  const prevVideo = () => setTheatreIndex((i) => (i - 1 + theatrePlaylist.length) % theatrePlaylist.length);
  const theatreVideo = theatrePlaylist[theatreIndex];

  // Weather sounds: browsers only allow audio after the visitor interacts, so start on the first
  // tap/click/key. iOS Safari unlocks audio on the END of a tap (touchend/click), so listen to those
  // too; until the audio is running, every interaction tries again.
  const soundSuspended = isTheatre || galleryScene === 'salon';
  const soundState = useRef({ on: weatherSoundOn, suspended: soundSuspended });
  soundState.current = { on: weatherSoundOn, suspended: soundSuspended };
  useEffect(() => {
    const events = ['pointerdown', 'pointerup', 'touchstart', 'touchend', 'click', 'keydown'];
    const start = () => {
      if (!weatherSound.current) {
        weatherSound.current = createWeatherSound(weatherRef, { enabled: soundState.current.on, suspended: soundState.current.suspended });
      }
      weatherSound.current?.resume();
      if (weatherSound.current?.isRunning()) events.forEach((e) => window.removeEventListener(e, start));
    };
    events.forEach((e) => window.addEventListener(e, start, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, start));
  }, []);
  useEffect(() => () => weatherSound.current?.dispose(), []);
  useEffect(() => { weatherSound.current?.setEnabled(weatherSoundOn); }, [weatherSoundOn]);
  useEffect(() => { weatherSound.current?.setSuspended(soundSuspended); }, [soundSuspended]);

  // Auto climate: after a random time, switch to a random climate that wasn't one of the last two.
  useEffect(() => {
    if (climateMode !== 'auto' || isLoading) return;
    const t = setTimeout(() => {
      climateHistory.current = [climate, ...climateHistory.current].slice(0, 2);
      setClimate(pickNextClimate(climateHistory.current));
    }, randomClimateDuration(AUTO_CLIMATE_MIN_MS, AUTO_CLIMATE_MAX_MS));
    return () => clearTimeout(t);
  }, [climateMode, climate, isLoading]);

  // Keep the address bar and tab title in sync with the selected painting so it can be shared.
  useEffect(() => {
    if (isFirstIndexRender.current) {
      isFirstIndexRender.current = false;
      if (linkedPaintingIndex === -1) return; // plain "/" visit: leave the URL alone
    }
    const painting = mockPaintings[currentPaintingIndex];
    navigate(`/painting/${paintingSlug(painting)}`, { replace: true });
    document.title = `${painting.title} – ${SITE_TITLE}`;
  }, [currentPaintingIndex]);

  // Guided tour: focus the current painting, then move on to the next one every few seconds.
  useEffect(() => {
    if (!isTourActive) return;
    setFocusOnPainting(true);
    const t = setTimeout(() => {
      setCurrentPaintingIndex((i) => (i + 1) % mockPaintings.length);
    }, TOUR_STEP_MS);
    return () => clearTimeout(t);
  }, [isTourActive, currentPaintingIndex]);

  const startTour = () => {
    setShowList(false);
    setShowInfo(false);
    setMusicPlaySignal((n) => n + 1);
    setIsTourActive(true);
  };
  const stopTour = () => setIsTourActive(false);
  const toggleTour = () => (isTourActive ? stopTour() : startTour());

  // Any manual movement by the visitor ends the tour.
  useEffect(() => {
    if (!isTourActive) return;
    const movementKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'KeyW', 'KeyA', 'KeyS', 'KeyD'];
    const onKey = (e) => { if (!isTypingTarget(e.target) && movementKeys.includes(e.code)) setIsTourActive(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isTourActive]);

  useEffect(() => {
    // Handle fullscreen changes
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const handlePaintingClick = (painting) => {
    if (isTheatre) return;
    setIsTourActive(false);
    const index = mockPaintings.findIndex(p => p.id === painting.id);
    if (index !== -1) {
      setCurrentPaintingIndex(index);
      setFocusOnPainting(true);
      setHoveredPainting(null); // Hide tooltip when painting is selected
      // Don't auto-open info popup, just focus on the painting
    }
  };

  const handlePaintingChange = (newIndex) => {
    setCurrentPaintingIndex(newIndex);
    // if (isGuideMode) {
      setFocusOnPainting(true);
    // }
  };

  const handlePaintingHover = (painting) => {
    if (!isMobile && !isTheatre) {
      setHoveredPainting(painting);
    }
  };

  const handlePaintingHoverOut = () => {
    setHoveredPainting(null);
  };



  const handleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleVideoToggle = () => {
    setVideoPlaying(!videoPlaying);
  };

  // const handleCameraMove = (direction) => {
  //   console.log('handleCameraMove:', direction);
  //   setManualMovement(direction);
  //   setFocusOnPainting(false); // Disable auto-focus when manually moving
  // };

  // FIX 1: New handler to START continuous movement when a UI button is pressed.
  const handleStartCameraMove = (direction) => {
    setIsTourActive(false);
    setFocusOnPainting(false); // Ensure manual movement overrides auto-focus.
    setUiMovement((prev) => ({ ...prev, [direction]: true }));
  };
  
  // FIX 1: New handler to STOP continuous movement when a UI button is released.
  const handleStopCameraMove = (direction) => {
    setUiMovement((prev) => ({ ...prev, [direction]: false }));
  };

  const handleMovementComplete = () => {
    // setManualMovement(null);
    setFocusOnPainting(false);
    setFloorClickPosition(null);
    setMoveToCenter(false);
  };

  const handleToggleGuideMode = () => {
    // Guide mode temporarily disabled
    console.log("Guide mode temporarily disabled");
    // const newGuideMode = !isGuideMode;
    // setIsGuideMode(newGuideMode);
    // setFocusOnPainting(false);
    // setFloorClickPosition(null);
    // setHoveredPainting(null);
    
    // if (newGuideMode) {
    //   // If enabling guide mode, automatically go to first painting
    //   setCurrentPaintingIndex(0);
    //   setFocusOnPainting(true);
    // } else {
    //   // If disabling guide mode, move camera to center of hall
    //   setMoveToCenter(true);
    // }
  };

  const handlePaintingDoubleClick = (painting) => {
    if (isTheatre) return;
    setViewerPainting(painting);
    setShowImageViewer(true);
  };

  const handleCloseImageViewer = () => {
    setShowImageViewer(false);
    setTimeout(() => setViewerPainting(null), 300);
  };


  const handleFloorClick = (point) => {
    if (!isGuideMode && !isTheatre) {
      // Move camera to clicked floor position
      setIsTourActive(false);
      setFloorClickPosition(point);
      setFocusOnPainting(false);
      setHoveredPainting(null); // Clear tooltip when clicking floor
    }
  };

  const handleUnselectPainting = () => {
    setFocusOnPainting(false);
    setShowInfo(false); // Close info popup when deselecting
  };

  const handleHintClick = () => {
    setShowNavHint(true);
    setTimeout(() => setShowNavHint(false), 4000);
  };

  // const handleKeyDown = (e) => {
  //   if (showList || showInfo) return;
    
  //   switch(e.key) {
  //     case 'ArrowLeft':
  //       e.preventDefault();
  //       handleCameraMove('left');
  //       break;
  //     case 'ArrowRight':
  //       e.preventDefault();
  //       handleCameraMove('right');
  //       break;
  //     case 'ArrowUp':
  //       e.preventDefault();
  //       handleCameraMove('forward');
  //       break;
  //     case 'ArrowDown':
  //       e.preventDefault();
  //       handleCameraMove('backward');
  //       break;
  //     case 'w':
  //     case 'W':
  //       e.preventDefault();
  //       handleCameraMove('forward');
  //       break;
  //     case 's':
  //     case 'S':
  //       e.preventDefault();
  //       handleCameraMove('backward');
  //       break;
  //     case 'a':
  //     case 'A':
  //       e.preventDefault();
  //       handleCameraMove('left');
  //       break;
  //     case 'd':
  //     case 'D':
  //       e.preventDefault();
  //       handleCameraMove('right');
  //       break;
  //     case 'g':
  //     case 'G':
  //       e.preventDefault();
  //       // handleToggleGuideMode(); // Guide mode disabled
  //       break;
  //     case 'Escape':
  //       if (isFullscreen) {
  //         document.exitFullscreen?.();
  //       }
  //       break;
  //     case 'f':
  //     case 'F':
  //       handleFullscreen();
  //       break;
  //     case 'l':
  //     case 'L':
  //       setShowList(true);
  //       break;
  //     case 'i':
  //     case 'I':
  //       setShowInfo(true);
  //       break;
  //   }
  // };

  // useEffect(() => {
  //   window.addEventListener('keydown', handleKeyDown);
  //   return () => window.removeEventListener('keydown', handleKeyDown);
  // }, [currentPaintingIndex, showList, showInfo, isFullscreen, isGuideMode]);

  // NEW: A simplified keydown listener just for app-level shortcuts (fullscreen, modals).
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isTheatre) { // only the theatre controls work while seated
        if (e.key === 'Escape') exitTheatre();
        else if (e.key === 'ArrowRight') nextVideo();
        else if (e.key === 'ArrowLeft') prevVideo();
        return;
      }
      if (showList || showInfo || showDonate || showGuestbook || isTypingTarget(e.target)) return;
      
      switch(e.key.toLowerCase()) {
        case 'escape':
          if (showHelp) setShowHelp(false);
          else if (isTourActive) setIsTourActive(false);
          else if (isFullscreen) document.exitFullscreen?.();
          break;
        case 't':
          {
            const wasTouring = isTourActive;
            toggleTour();
            rememberShortcut(() => (wasTouring ? startTour() : stopTour()));
          }
          break;
        case 'n':
          nextTimeOfDay();
          rememberShortcut(() => setTimeOfDay((t) => TIME_IDS[(TIME_IDS.indexOf(t) + TIME_IDS.length - 1) % TIME_IDS.length]));
          break;
        case 'f':
          handleFullscreen();
          break;
        case 'l':
          setShowList(true);
          break;
        case 'i':
          setShowInfo(true);
          rememberShortcut(() => setShowInfo(false));
          break;
        case 'h':
          setShowHelp((v) => !v);
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showList, showInfo, showDonate, showGuestbook, showHelp, isFullscreen, isTourActive, isTheatre]);

  // Easter eggs (see lib/eggs.js): typed codes, the birthday, the night owl and a fireworks preview.
  const [paintTrigger, setPaintTrigger] = useState(0);
  // Mobile joysticks: left walks, right looks around (read every frame by the camera controller).
  const joystickRef = useRef({ mx: 0, my: 0, lx: 0, ly: 0 });
  const [isTouchScreen] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches);
  // Typing "paint" presses the a/i/n/t shortcuts on the way; when the word is complete, those
  // shortcuts (fired in the last few seconds) are undone, so the secret leaves no side effects.
  const recentShortcuts = useRef([]);
  const rememberShortcut = (undo) => {
    const now = Date.now();
    recentShortcuts.current = [...recentShortcuts.current.filter((x) => now - x.at < 4000), { at: now, undo }];
  };
  const undoRecentShortcuts = () => {
    const now = Date.now();
    recentShortcuts.current.filter((x) => now - x.at < 4000).reverse().forEach((x) => x.undo());
    recentShortcuts.current = [];
  };
  useEffect(() => {
    const KONAMI = ['arrowup', 'arrowup', 'arrowdown', 'arrowdown', 'arrowleft', 'arrowright', 'arrowleft', 'arrowright', 'b', 'a'];
    let keys = [];
    let typed = '';
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return; // not while writing in the guestbook
      const k = (e.key || '').toLowerCase();
      keys = [...keys, k].slice(-KONAMI.length);
      if (keys.join() === KONAMI.join()) {
        keys = [];
        eggState.konamiUntil = performance.now() + 9000;
        playChime();
        announceEgg('konami', 'Up, up, down, down… the paintings dance!', '🎉');
      }
      if (k.length === 1) {
        typed = (typed + k).slice(-5);
        if (typed === 'paint') {
          typed = '';
          // after this key's own shortcut has run (it's handled by another listener), undo them all
          setTimeout(() => {
            undoRecentShortcuts();
            setPaintTrigger(Date.now());
            announceEgg('paint', 'A splash of colour, and a few words about art.', '🎨');
          }, 0);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  useEffect(() => {
    if (isLoading) return undefined;
    const timers = [];
    if (isBirthdayToday()) {
      eggState.celebrate = true;
      timers.push(setTimeout(() => announceEgg('birthday', "It's David's birthday today: balloons for everyone! Leave him a wish in the guestbook.", '🎈'), 2500));
    }
    if (isOwlHour()) {
      eggState.owl = true;
      timers.push(setTimeout(() => announceEgg('owl', "Night owl, aren't you? Someone on the glass roof is keeping you company.", '🦉'), 4000));
    }
    if (/[?&]fireworks\b/.test(window.location.search)) timers.push(setTimeout(() => celebrateLikes(0), 3000));
    return () => timers.forEach(clearTimeout);
  }, [isLoading]);

  return (
    <div className="h-screen w-full bg-gray-100 overflow-hidden full-height">
      <EggToast />
      <SecretsHint active={sceneShown && !isTheatre} onOpenHelp={() => setShowHelp(true)} />
      <PaintSplash trigger={paintTrigger} />
      {/* Phones and tablets: game-style joysticks to walk (left) and look around (right) */}
      {(isMobile || isTouchScreen) && !isLoading && !isTheatre && !sceneFade && (
        <>
          <VirtualJoystick label="Walk" style={{ left: 16, bottom: 24 }} onChange={(x, y) => { joystickRef.current.mx = x; joystickRef.current.my = y; }} />
          <VirtualJoystick label="Look" style={{ right: 16, bottom: 24 }} onChange={(x, y) => { joystickRef.current.lx = x; joystickRef.current.ly = y; }} />
        </>
      )}
      {isLoading ? (
        <LoadingScreen />
      ) : (
        <>

          <ImageViewer
            isOpen={showImageViewer}
            onClose={handleCloseImageViewer}
            imageUrl={viewerPainting?.imageUrl}
            title={viewerPainting?.title}
            artist={viewerPainting?.artist}
          />

          {galleryScene === 'salon' ? (
            <SceneErrorBoundary onError={handleSalonError}>
              <Suspense fallback={null}>
                <GrandSalon
                  currentPaintingIndex={currentPaintingIndex}
                  onPaintingClick={handlePaintingClick}
                  onPaintingDoubleClick={handlePaintingDoubleClick}
                  onPaintingHover={handlePaintingHover}
                  onPaintingHoverOut={handlePaintingHoverOut}
                  onMovementComplete={handleMovementComplete}
                  focusOnPainting={focusOnPainting}
                  onFloorClick={handleFloorClick}
                  onUnselectPainting={handleUnselectPainting}
                  floorClickPosition={floorClickPosition}
                  moveToCenter={moveToCenter}
                  uiMovement={uiMovement}
                  isNight={isNight}
                  walkTo={doorWalk}
                  onWalkDone={handleDoorWalkDone}
                  joystickRef={joystickRef}
                  exitDoorOpen={doorOpen}
                  onExit={exitSalon}
                  onDoorHover={doorHover('The Glass Hall')}
                  onDoorHoverOut={handlePaintingHoverOut}
                  onReady={handleSceneReady}
                />
              </Suspense>
            </SceneErrorBoundary>
          ) : (
          <Gallery3D
            currentPaintingIndex={currentPaintingIndex}
            onPaintingClick={handlePaintingClick}
            onPaintingDoubleClick={handlePaintingDoubleClick} 
            onPaintingHover={handlePaintingHover}
            onPaintingHoverOut={handlePaintingHoverOut}
            // manualMovement={manualMovement}
            onMovementComplete={handleMovementComplete}
            focusOnPainting={focusOnPainting}
            isGuideMode={isGuideMode}
            onFloorClick={handleFloorClick}
            onUnselectPainting={handleUnselectPainting}
            floorClickPosition={floorClickPosition}
            moveToCenter={moveToCenter}
            uiMovement={uiMovement} // FIX 1: Pass the new UI movement state down to the 3D scene.
            isNight={isNight}
            timeOfDay={timeOfDay}
            localTimeOfDay={localTimeOfDay}
            joystickRef={joystickRef}
            climate={climate}
            weatherRef={weatherRef}
            theatreMode={isTheatre}
            onTheatreSeated={setTheatreSeated}
            theatreVideo={isTheatre && theatreSeated && !isMobile
              ? <TheatrePlayer url={theatreVideo.youtubeUrl} title={theatreVideo.title} onEnded={nextVideo} />
              : null}
            salonDoorOpen={doorOpen}
            onEnterSalon={enterSalon}
            onDoorHover={doorHover('The Grand Salon')}
            onDoorHoverOut={handlePaintingHoverOut}
            walkTo={doorWalk || windowWalk}
            onWalkDone={doorWalk ? handleDoorWalkDone : () => setWindowWalk(null)}
            onWindowClick={lookOutWindow}
            onWindowHover={windowHover}
            onWindowHoverOut={handlePaintingHoverOut}
            spawnAtDoor={cameFromSalon}
            onReady={handleSceneReady}
          />
          )}

          {/* Fade through black while moving between the glass hall and the Grand Salon */}
          {/* Turns black instantly (no CSS transition), so it is opaque even if the browser is busy
              swapping scenes; fades back in smoothly once the new room is ready. */}
          <div
            className={`fixed inset-0 z-[60] bg-black flex items-center justify-center ${sceneFade ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
            style={{ transition: sceneFade ? 'none' : 'opacity 0.6s ease-out' }}
          >
            {sceneFade && (
              <div className="text-white/80 text-lg tracking-widest uppercase fade-in">
                {fadeLabel}
              </div>
            )}
          </div>
          {sceneNotice && (
            <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[61] bg-black/75 text-white text-sm px-4 py-2 rounded-full">{sceneNotice}</div>
          )}

          {/* Theatre mode: phones get a regular full-width player over the darkened hall */}
          {isTheatre && theatreSeated && isMobile && (
            <div className="fixed inset-x-0 top-1/2 -translate-y-1/2 z-40 px-2">
              <div className="w-full aspect-video bg-black rounded-lg overflow-hidden shadow-2xl border border-white/10">
                <TheatrePlayer url={theatreVideo.youtubeUrl} title={theatreVideo.title} onEnded={nextVideo} />
              </div>
            </div>
          )}
          {isTheatre && (
            <TheatreControls
              seated={theatreSeated}
              video={theatreVideo}
              index={theatreIndex}
              total={theatrePlaylist.length}
              onPrev={prevVideo}
              onNext={nextVideo}
              onExit={exitTheatre}
              isMobile={isMobile}
            />
          )}

          
          {!isTheatre && <NavigationControls
            onEnterTheatre={enterTheatre}
            inSalon={galleryScene === 'salon'}
            onSwitchRoom={galleryScene === 'salon' ? exitSalon : enterSalon}
            currentPaintingIndex={currentPaintingIndex}
            onPaintingChange={handlePaintingChange}
            onShowList={() => setShowList(true)}
            onShowInfo={() => setShowInfo(true)}
            onFullscreen={handleFullscreen}
            // onCameraMove={handleCameraMove}
            onStartCameraMove={handleStartCameraMove}
            onStopCameraMove={handleStopCameraMove}
            isMobile={isMobile}
            isGuideMode={isGuideMode}
            onToggleGuideMode={handleToggleGuideMode}
            isTourActive={isTourActive}
            onToggleTour={toggleTour}
            timeOfDay={timeOfDay}
            localTimeOfDay={localTimeOfDay}
            onTimeOfDayChange={setTimeOfDay}
            climateMode={climateMode}
            climate={climate}
            localWeather={localWeather}
            onClimateChange={changeClimateMode}
            autoClimateSeconds={null}
            weatherSoundOn={weatherSoundOn}
            onToggleWeatherSound={toggleWeatherSound}
            likes={likes}
            onToggleLike={toggleLike}
            musicSlotRef={setMusicSlot}
          />}

          {isTourActive && (
            <TourOverlay
              painting={mockPaintings[currentPaintingIndex]}
              index={currentPaintingIndex}
              total={mockPaintings.length}
              stepMs={TOUR_STEP_MS}
              onStop={stopTour}
            />
          )}
          
          <AudioPlayer
            audioUrl={audioUrl}
            title="Classical Gallery Music"
            playSignal={musicPlaySignal}
            suspended={isTheatre}
            slot={musicSlot}
          />

          {/* Desktop Hover Tooltip */}
          {!isMobile && <HoverTooltip painting={hoveredPainting} />}

          {/* Mobile Touch Instructions */}
          {isMobile && showInstructions && (
            <div className="fixed bottom-28 left-1/2 transform -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-lg text-sm pointer-events-none z-40 fade-in text-center w-max max-w-[90vw]">
              Walk and Look sticks to move • Tap the floor to walk there
            </div>
          )}
          
          {/* Keyboard shortcuts hint */}
          {/* {!isMobile && (
            <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-lg text-xs pointer-events-none z-40 opacity-70">
              WASD/Arrows: Move • Drag: Look • Click Floor: Move • Click Art: Focus • F: Fullscreen
            </div>
          )} */}

          {/* Artwork List Modal */}
          {showList && (
            <PaintingList
              isOpen={showList}
              onClose={() => setShowList(false)}
              currentPaintingIndex={currentPaintingIndex}
              onPaintingSelect={handlePaintingChange}
            />
          )}

          {/* Painting Info Modal */}
          {showInfo && (
            <PaintingInfo
              isOpen={showInfo}
              onClose={() => setShowInfo(false)}
              currentPaintingIndex={currentPaintingIndex}
              videoPlaying={videoPlaying}
              onVideoToggle={handleVideoToggle}
              onDonate={() => { setShowInfo(false); setShowDonate(true); }}
              likes={likes}
              onToggleLike={toggleLike}
            />
          )}

          <div className={`fixed top-4 left-4 z-50 flex flex-col space-y-2 ${isTheatre ? 'hidden' : ''}`}>
              {/* NEW: Profile Icon Button */}
              {profileWebpageUrl !== "https://your-website.com" && ( // Only show if URL is set
                  <a
                      href={profileWebpageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-10 w-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center text-amber-800 hover:bg-amber-100 transition-all overflow-hidden border border-gray-200 group"
                      title="Visit Artist's Website"
                  >
                      <img
                          src={profileImageUrl}
                          alt="Artist profile picture"
                          className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-300"
                          // Basic fallback in case image fails
                          onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/40x40/cccccc/FFFFFF?text=X"; }}
                      />
                  </a>
              )}

              {/* Support the Artist (donate), right under the profile picture */}
              <button
                  onClick={() => setShowDonate(true)}
                  className="h-10 w-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors"
                  title="Support the Artist"
                  aria-label="Support the Artist"
              >
                  <Heart size={20} className="fill-red-500" />
              </button>

              {/* Help Icon Button */}
              <button
                  onClick={() => setShowHelp(true)}
                  className="h-10 w-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center text-amber-800 hover:bg-amber-100 transition-colors"
                  title="Help & Navigation Guide (H)"
              >
                  <HelpCircle size={22} />
              </button>

              {/* Guestbook */}
              <button
                  onClick={() => setShowGuestbook(true)}
                  className="h-10 w-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center text-amber-800 hover:bg-amber-100 transition-colors"
                  title="Guestbook"
              >
                  <BookOpen size={20} />
              </button>

          </div>

          {/* Help Icon - Top Left */}
          {/* <button
            onClick={() => setShowHelp(true)}
            className={`fixed top-4 left-4 h-10 w-10 bg-white/90 backdrop-blur-sm rounded-full shadow-lg flex items-center justify-center text-amber-800 hover:bg-amber-100 transition-all z-40`}
            title="Help & Navigation Guide"
          >
            <HelpCircle size={18} />
          </button> */}

          {/* Help Modal */}
          <HelpModal
            isOpen={showHelp}
            onClose={() => setShowHelp(false)}
            isMobile={isMobile}
          />

          <GuestbookModal isOpen={showGuestbook} onClose={() => setShowGuestbook(false)} />

          <DonateModal
            isOpen={showDonate}
            onClose={() => setShowDonate(false)}
            onOpenGuestbook={() => { setShowDonate(false); setShowGuestbook(true); }}
          />
        </>
      )}
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<GalleryApp />} />
          <Route path="/painting/:slug" element={<GalleryApp />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;